import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Vector2, Vector3 } from "three";
import type { AnnotationDocument, AnnotationItem, AnnotationPosition, ModelCoordinateMetadata, SpatialCompatibility } from "@factory-twin/contracts";
import type { ApiClient, ModelVariantSelection } from "../lib/api";
import { addAnnotation, compatibleSpatialDocument, deleteAnnotation, downloadAnnotationJson, emptyAnnotationDocument, isAnnotationDisplaySafe, reanchorAnnotation, updateAnnotation } from "../lib/annotations";
import { inferSceneFormat } from "../lib/splat-format";
import { VIEWER_LOAD_STALL_TIMEOUT_MS } from "../lib/viewer-performance";
import { selectViewerProfile, viewerCapabilitiesFromBrowser } from "../lib/viewer-profile";
import { frameSplatScene, type FrameableSplatViewer } from "../lib/viewer-camera";
import { AnnotationEditor } from "./AnnotationEditor";

interface Props {
  api: ApiClient;
  sourceUrl: string;
  sceneId: string | null;
  sourceName: string;
  model: ModelCoordinateMetadata;
  variant?: ModelVariantSelection;
  onExit: () => void;
  onRequestLowQuality?: () => Promise<void>;
}

interface ViewerHandle extends FrameableSplatViewer {
  raycaster: { setFromCameraAndScreenPosition: (camera: object, position: Vector2, dimensions: Vector2) => void; intersectSplatMesh: (mesh: unknown, hits: Array<{ origin: Vector3; distance: number }>) => void };
  addSplatScene: (path: string, options: Record<string, unknown>) => Promise<void> & { abort?: (reason?: unknown) => void };
  start: () => void;
  dispose: () => Promise<void>;
}

type LoadPhase = "loading" | "streaming" | "ready" | "cancelled" | "failed";
interface LoadState { phase: LoadPhase; progress: number | null; label: string; firstRenderableMs: number | null; fullyLoadedMs: number | null; }

const colors = { safety: "#ff665e", procedure: "#54c8ff", equipment: "#f6b84d", note: "#9da9ad" } as const;
const newLoadState = (): LoadState => ({ phase: "loading", progress: 0, label: "接続を開始しています…", firstRenderableMs: null, fullyLoadedMs: null });
const progressLabel = (state: LoadState) => {
  const percentage = state.progress === null ? "" : `${Math.round(state.progress)}%`;
  return percentage && state.label.includes(percentage) ? state.label : `${state.label}${percentage ? ` ${percentage}` : ""}`;
};

export function SceneViewer({ api, sourceUrl, sceneId, sourceName, model, variant, onExit, onRequestLowQuality }: Props) {
  const mount = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<ViewerHandle | null>(null);
  const abortLoadRef = useRef<(() => void) | null>(null);
  const pinElements = useRef(new Map<string, HTMLButtonElement>());
  const annotationItemsRef = useRef<AnnotationItem[]>([]);
  const pointerDown = useRef<{ x: number; y: number } | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [placementMode, setPlacementMode] = useState(false);
  const [documentState, setDocument] = useState<AnnotationDocument>(() => emptyAnnotationDocument(sceneId ?? "00000000-0000-4000-8000-000000000000", model));
  const [compatibility, setCompatibility] = useState<SpatialCompatibility>(compatibleSpatialDocument);
  const [manualQueue, setManualQueue] = useState<AnnotationItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"saved" | "dirty" | "saving">("saved");
  const [loadEnabled, setLoadEnabled] = useState(true);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [forceLowQuality, setForceLowQuality] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>(newLoadState);
  const [capabilities] = useState(() => viewerCapabilitiesFromBrowser());
  const profile = useMemo(() => selectViewerProfile(capabilities, forceLowQuality), [capabilities, forceLowQuality]);
  const annotationItems = useMemo(() => isAnnotationDisplaySafe(compatibility) ? documentState.annotations : [], [compatibility, documentState.annotations]);
  annotationItemsRef.current = annotationItems;

  useEffect(() => {
    if (!sceneId) return;
    api.getAnnotations(sceneId).then(({ document, compatibility: next }) => { setDocument(document); setCompatibility(next); }).catch((reason: Error) => setError(reason.message));
  }, [api, sceneId]);

  useEffect(() => {
    if (!loadEnabled) return;
    let disposed = false;
    let cancelled = false;
    let current: ViewerHandle | null = null;
    let viewerRoot: HTMLDivElement | null = null;
    let stallTimer: number | undefined;
    let firstRenderable = false;
    let fullyLoaded = false;
    const startedAt = performance.now();
    let abortCurrent: () => void = () => {};
    const clearStallTimer = () => { if (stallTimer !== undefined) window.clearTimeout(stallTimer); stallTimer = undefined; };
    const failLoad = (message: string) => {
      if (disposed || cancelled) return;
      cancelled = true;
      abortCurrent();
      clearStallTimer();
      setReady(false);
      setError(message);
      setLoadState((previous) => ({ ...previous, phase: "failed" }));
      setLoadEnabled(false);
    };
    const armStallTimer = () => {
      clearStallTimer();
      if (!fullyLoaded) stallTimer = window.setTimeout(() => failLoad("モデルの読み込みが1分間進みませんでした。通信を確認して再試行してください。"), VIEWER_LOAD_STALL_TIMEOUT_MS);
    };
    const handleContextLost = (event: Event) => { event.preventDefault(); failLoad("WebGLの描画コンテキストを失いました。軽量表示で再試行してください。"); };
    async function initialize() {
      if (!mount.current) return;
      armStallTimer();
      try {
        const GaussianSplats3D = await import("@mkkellogg/gaussian-splats-3d");
        if (disposed || !mount.current) return;
        viewerRoot = document.createElement("div");
        viewerRoot.className = "viewer-render-root";
        viewerRoot.addEventListener("webglcontextlost", handleContextLost, true);
        mount.current.appendChild(viewerRoot);
        current = new GaussianSplats3D.Viewer({
          rootElement: viewerRoot,
          cameraUp: [0, -1, -0.6],
          initialCameraPosition: [-2.8, -4.5, 3.2],
          initialCameraLookAt: [0, 0, 0],
          sharedMemoryForWorkers: false,
          sphericalHarmonicsDegree: profile.sphericalHarmonicsDegree,
          antialiased: profile.antialiased,
          ignoreDevicePixelRatio: profile.ignoreDevicePixelRatio,
          inMemoryCompressionLevel: profile.inMemoryCompressionLevel,
          freeIntermediateSplatData: true,
          gpuAcceleratedSort: false,
          injectBuiltInStyles: false,
        }) as unknown as ViewerHandle;
        viewerRef.current = current;
        const format = inferSceneFormat(sourceUrl, sourceName, GaussianSplats3D.SceneFormat);
        const progressiveLoad = profile.progressiveLoad && format !== GaussianSplats3D.SceneFormat.Spz;
        const loadingPromise = current.addSplatScene(sourceUrl, {
          splatAlphaRemovalThreshold: profile.splatAlphaRemovalThreshold,
          showLoadingUI: false,
          progressiveLoad,
          onProgress: (percent: number, label: string) => {
            if (disposed || cancelled) return;
            armStallTimer();
            const progress = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : null;
            if (progress === 100) fullyLoaded = true;
            setLoadState((previous) => {
              const phase = firstRenderable ? progress === 100 ? "ready" : "streaming" : "loading";
              if (previous.progress !== null && progress !== null && Math.abs(previous.progress - progress) < 1 && previous.label === label && previous.phase === phase) return previous;
              return { ...previous, phase, progress, label: label || "モデルを処理しています…", fullyLoadedMs: progress === 100 ? Math.round(performance.now() - startedAt) : previous.fullyLoadedMs };
            });
          },
          ...(format === undefined ? {} : { format }),
        });
        abortCurrent = () => loadingPromise.abort?.("Viewer loading cancelled");
        abortLoadRef.current = () => { cancelled = true; abortCurrent(); };
        await loadingPromise;
        if (disposed || cancelled || !current) return;
        firstRenderable = true;
        frameSplatScene(current);
        current.start();
        const firstRenderableMs = Math.round(performance.now() - startedAt);
        performance.mark("factory-twin-viewer-first-render");
        setReady(true);
        mount.current?.focus({ preventScroll: true });
        setLoadState((previous) => ({ ...previous, phase: fullyLoaded || !progressiveLoad ? "ready" : "streaming", firstRenderableMs, fullyLoadedMs: fullyLoaded || !progressiveLoad ? Math.round(performance.now() - startedAt) : previous.fullyLoadedMs }));
        if (fullyLoaded || !progressiveLoad) clearStallTimer();
      } catch (reason) {
        if (!disposed && !cancelled) failLoad(reason instanceof Error ? reason.message : "3DGSモデルを読み込めませんでした");
      }
    }
    void initialize();
    return () => {
      disposed = true;
      clearStallTimer();
      abortCurrent();
      abortLoadRef.current = null;
      viewerRef.current = null;
      viewerRoot?.removeEventListener("webglcontextlost", handleContextLost, true);
      if (current && viewerRoot) {
        viewerRoot.hidden = true;
        if (viewerRoot.parentNode !== document.body) document.body.appendChild(viewerRoot);
        void current.dispose();
      } else viewerRoot?.remove();
    };
  }, [loadAttempt, loadEnabled, profile, sourceName, sourceUrl]);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      const viewer = viewerRef.current;
      const element = mount.current;
      if (viewer && element && ready) {
        viewer.camera.updateMatrixWorld();
        const bounds = element.getBoundingClientRect();
        for (const annotation of annotationItemsRef.current) {
          const pin = pinElements.current.get(annotation.id);
          if (!pin) continue;
          const projected = new Vector3(annotation.position.x, annotation.position.y, annotation.position.z).project(viewer.camera as never);
          const visible = projected.z > -1 && projected.z < 1;
          pin.hidden = !visible;
          if (visible) pin.style.transform = `translate3d(${(projected.x + 1) * bounds.width / 2}px, ${(-projected.y + 1) * bounds.height / 2}px, 0)`;
        }
      }
      frame = requestAnimationFrame(update);
    };
    frame = requestAnimationFrame(update);
    return () => cancelAnimationFrame(frame);
  }, [ready]);

  const setPinElement = useCallback((annotationId: string, element: HTMLButtonElement | null) => { if (element) pinElements.current.set(annotationId, element); else pinElements.current.delete(annotationId); }, []);
  const retryLoading = () => { setError(""); setReady(false); setLoadState(newLoadState()); setLoadEnabled(true); setLoadAttempt((attempt) => attempt + 1); };
  const cancelLoading = () => { abortLoadRef.current?.(); setReady(false); setLoadState((previous) => ({ ...previous, phase: "cancelled", label: "読み込みを中止しました" })); setLoadEnabled(false); };
  async function retryWithLowQuality() {
    setForceLowQuality(true);
    setLoadEnabled(false);
    try { await onRequestLowQuality?.(); } catch (reason) { setError(reason instanceof Error ? reason.message : "軽量モデルの取得に失敗しました"); setLoadState((previous) => ({ ...previous, phase: "failed" })); return; }
    retryLoading();
  }

  function place(event: React.PointerEvent<HTMLDivElement>) {
    if (!placementMode || !viewerRef.current || !mount.current || !pointerDown.current || Math.hypot(event.clientX - pointerDown.current.x, event.clientY - pointerDown.current.y) > 4) return;
    const bounds = mount.current.getBoundingClientRect();
    const hits: Array<{ origin: Vector3; distance: number }> = [];
    viewerRef.current.raycaster.setFromCameraAndScreenPosition(viewerRef.current.camera, new Vector2(event.clientX - bounds.left, event.clientY - bounds.top), new Vector2(bounds.width, bounds.height));
    viewerRef.current.raycaster.intersectSplatMesh(viewerRef.current.getSplatMesh(), hits);
    if (!hits[0]) { setError("この位置ではモデル表面を検出できません。スプラット上をクリックしてください。"); return; }
    const target: AnnotationPosition = { x: hits[0].origin.x, y: hits[0].origin.y, z: hits[0].origin.z };
    const pending = manualQueue[0];
    const next = pending ? reanchorAnnotation(documentState, pending, target) : addAnnotation(documentState, target);
    setDocument(next);
    setSelectedId(pending ? null : next.annotations.at(-1)?.id ?? null);
    if (pending) setManualQueue((current) => current.slice(1));
    setSaveState("dirty");
    setPlacementMode(Boolean(manualQueue[1]));
    setError("");
  }

  function handleCanvasKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") { event.preventDefault(); setPlacementMode(false); setSelectedId(null); setError(""); }
  }
  async function save() {
    if (!sceneId) { downloadAnnotationJson(documentState); setSaveState("saved"); return; }
    setSaveState("saving");
    try { const result = await api.saveAnnotations(documentState); setDocument(result.document); setSaveState("saved"); } catch (reason) { setSaveState("dirty"); setError(reason instanceof Error ? reason.message : "保存に失敗しました"); }
  }
  async function applySuggestedTransform() {
    if (!sceneId) return;
    try { const result = await api.reanchorAnnotations(sceneId, "APPLY_TRANSFORM"); setDocument(result.document); setCompatibility(result.compatibility); setSaveState("saved"); } catch (reason) { setError(reason instanceof Error ? reason.message : "推定変換の適用に失敗しました"); }
  }
  async function discardStaleAnnotations() {
    if (!sceneId || !window.confirm("旧モデルの注釈をすべて破棄しますか？この操作は元に戻せません。")) return;
    try { const result = await api.reanchorAnnotations(sceneId, "DISCARD"); setDocument(result.document); setCompatibility(result.compatibility); setSaveState("saved"); } catch (reason) { setError(reason instanceof Error ? reason.message : "注釈の破棄に失敗しました"); }
  }
  function startManualReanchor() {
    setManualQueue(documentState.annotations); setDocument(emptyAnnotationDocument(documentState.sceneId, model)); setCompatibility(compatibleSpatialDocument()); setSelectedId(null); setPlacementMode(documentState.annotations.length > 0); setSaveState("dirty");
  }

  const selected = useMemo(() => documentState.annotations.find((item) => item.id === selectedId) ?? null, [documentState.annotations, selectedId]);
  const canDisplayAnnotations = isAnnotationDisplaySafe(compatibility);
  const canRetryLoad = loadState.phase === "failed" || loadState.phase === "cancelled";
  const variantLabel = variant?.served === "low" ? "軽量LOD" : variant?.fallback ? "標準モデル（軽量LODなし）" : profile.label;
  const changeSelected = (annotation: AnnotationItem) => { setDocument((current) => updateAnnotation(current, annotation)); setSaveState("dirty"); };

  return <main className={`viewer-shell ${placementMode ? "placing" : ""}`} data-viewer-profile={profile.deviceClass} data-model-variant={variant?.served ?? "local"} data-load-state={loadState.phase} data-first-render-ms={loadState.firstRenderableMs ?? ""} data-full-load-ms={loadState.fullyLoadedMs ?? ""}>
    <div className="viewer-canvas" ref={mount} tabIndex={0} role="application" aria-label={`${sourceName} の3Dモデル。ドラッグで回転、右クリックで移動、ホイールでズームします。Escapeで注釈配置を終了します。`} onKeyDown={handleCanvasKeyDown} onPointerDown={(event) => { pointerDown.current = { x: event.clientX, y: event.clientY }; }} onPointerUp={place} />
    <header className="viewer-topbar"><button className="viewer-back" onClick={onExit} aria-label="ビューアを閉じて一覧へ戻る">←</button><div className="brand"><span className="brand-mark">FT</span><span>FACTORY TWIN</span></div><div className="viewer-title"><span>SCENE /</span><strong>{sourceName}</strong><small>{variantLabel}</small></div><div className="viewer-actions"><span className={saveState} aria-live="polite">{manualQueue.length > 0 ? `再アンカー残り ${manualQueue.length}` : saveState === "saved" ? "保存済み" : saveState === "saving" ? "保存中…" : "未保存"}</span><button disabled={manualQueue.length > 0 || !canDisplayAnnotations} onClick={() => void save()}>{sceneId ? "注釈を保存" : "JSONを書き出す"}</button></div></header>
    <aside className="viewer-sidebar" aria-label="注釈一覧"><div className="section-label">ANNOTATIONS <span>{documentState.annotations.length.toString().padStart(2, "0")}</span></div><button className={`place-button ${placementMode ? "active" : ""}`} aria-pressed={placementMode} disabled={!canDisplayAnnotations} onClick={() => setPlacementMode((value) => !value)}><i aria-hidden="true">＋</i><span><strong>{placementMode ? "モデル上をクリック" : "注釈ピンを追加"}</strong><small>{manualQueue[0] ? `再配置: ${manualQueue[0].title}` : placementMode ? "Escapeでキャンセル" : "3D座標に情報を配置"}</small></span></button><div className="annotation-list">{documentState.annotations.map((annotation, index) => <button key={annotation.id} className={selectedId === annotation.id ? "selected" : ""} aria-pressed={selectedId === annotation.id} onClick={() => setSelectedId(annotation.id)}><i style={{ background: colors[annotation.category] }}>{index + 1}</i><span><strong>{annotation.title}</strong><small>{annotation.category.toUpperCase()}</small></span></button>)}</div></aside>
    <div className="viewer-hud" aria-hidden="true"><span><b>LMB</b> 回転</span><span><b>RMB</b> 移動</span><span><b>WHEEL</b> ズーム</span><span><b>ESC</b> 配置終了</span></div><div className="viewer-axis" aria-hidden="true"><i>Z</i><span>Y</span><b>X</b></div>
    <output className="sr-only" aria-live="polite">{progressLabel(loadState)}</output>
    {loadState.phase === "loading" && <section className="viewer-loading" role="status" aria-label="3Dモデルを読み込み中"><div aria-hidden="true" /><strong>GAUSSIAN DATA STREAM</strong><span>{progressLabel(loadState)}</span>{loadState.progress !== null && <progress value={loadState.progress} max="100" aria-label={`読み込み進捗 ${Math.round(loadState.progress)}%`} />}<button onClick={cancelLoading}>読み込みを中止</button></section>}
    {loadState.phase === "streaming" && <section className="viewer-load-status" role="status"><span>{progressLabel(loadState)}</span><button onClick={cancelLoading}>中止</button></section>}
    {(error || loadState.phase === "cancelled") && <div className="viewer-error" role="alert"><span>{error || "モデルの読み込みを中止しました。"}</span><div>{canRetryLoad && <><button onClick={retryLoading}>再試行</button>{!forceLowQuality && <button onClick={() => void retryWithLowQuality()}>軽量表示で再試行</button>}</>}<button onClick={() => setError("")} aria-label="エラーを閉じる">×</button></div></div>}
    {!canDisplayAnnotations && <section className="coordinate-warning" role="alert"><strong>注釈の座標互換性を確認できません</strong><p>{compatibility.reason}</p>{compatibility.transformConfidence !== null && <p>推定変換の信頼度: {Math.round(compatibility.transformConfidence * 100)}%</p>}<div>{compatibility.suggestedTransformAvailable && <button onClick={() => void applySuggestedTransform()}>推定変換を採用</button>}<button onClick={startManualReanchor}>手動で再アンカー</button><button className="danger" onClick={() => void discardStaleAnnotations()}>旧注釈を破棄</button></div></section>}
    {canDisplayAnnotations && documentState.annotations.map((annotation, index) => <button key={annotation.id} ref={(element) => setPinElement(annotation.id, element)} className={`spatial-pin ${selectedId === annotation.id ? "selected" : ""}`} style={{ "--pin-color": colors[annotation.category] } as React.CSSProperties} aria-label={`注釈 ${index + 1}: ${annotation.title}`} onClick={() => setSelectedId(annotation.id)}><i aria-hidden="true">{index + 1}</i><span>{annotation.title}</span></button>)}
    {selected && <AnnotationEditor annotation={selected} onChange={changeSelected} onClose={() => setSelectedId(null)} onDelete={() => { setDocument((current) => deleteAnnotation(current, selected.id)); setSelectedId(null); setSaveState("dirty"); }} />}
  </main>;
}
